/**
 * isExportable — the single source of truth for WHICH contacts can be selected
 * for export, on the FE.
 *
 * Selectability rule (product decision 2026-06-10): a contact is selectable
 * UNLESS it is already in Thrive or blocked.
 *
 *   selectable   : imported (never exported), failed (retry), W2 identity
 *                  mismatch (the recruiter can Update anyway), a generic
 *                  `skipped` with no blocking reason.
 *   NOT selectable: a DISMISSED row (`left_out` — the recruiter said no),
 *                  exported (already in Thrive, W2 or not: nothing to
 *                  re-export), already-added (`skipped` with a Thrive id,
 *                  EXCEPT a W2 row, whose identity is still contested),
 *                  W1 `duplicate` (fix-in-Thrive first), and
 *                  `pending` (a run is in flight for this row).
 *
 * This is the FE mirror of the export worker's WRITE policy
 * (`resolve_export_plan` in `nu_assist_shared/domain/export.py`), NOT a status
 * display — which is why it still reads the raw columns rather than
 * `review_status` (sub-task 10 kept the write policy and the read projection
 * deliberately separate, and `review_status` cannot tell "skipped because
 * already added" apart from "matched, still addable to the search": both are
 * `already_in_thrive`). The DISPLAY derivation this file used to share
 * (`skipReason`) is deleted; what the recruiter sees is the server's
 * `review_status`, always.
 *
 * Backs both the Export panel checkboxes and the Import-Detail contacts table,
 * so the gating can never drift between the two surfaces.
 */

import type { ImportContactRow, W2Decision } from "@nu-assist/contracts";

type ExportableRow = Pick<
  ImportContactRow,
  "thrive_match_type" | "export_status" | "contact_id_thrive" | "is_w2" | "is_w3" | "left_out"
>;

export function isExportable(row: ExportableRow): boolean {
  // Rule 0, and FIRST for the same reason it is first server-side: a persisted
  // "Leave out" dismissal. `resolve_export_plan` has always skipped these with
  // `skip_reason=left_out_dismissed` whatever the FE sent; until this rule
  // existed the checkbox stayed live and the CTA still counted the row, so the
  // UI offered an action the server was always going to decline. Ahead of the
  // W2 escape hatch on purpose — a recruiter who dismissed the row has already
  // answered the identity question "Update anyway" exists to ask.
  if (row.left_out) return false;

  // W1 duplicate LinkedIn — unexportable as-is until fixed in Thrive.
  if (row.thrive_match_type === "duplicate") return false;

  // Already in Thrive from this run — nothing to re-export.
  if (row.export_status === "exported") return false;

  // Already written in a prior run: a run resolved the row to `skipped` while
  // it carries a Thrive id, i.e. the contact is in Thrive and the run had
  // nothing to do. (The write policy's resumability rule 2b is the backend
  // twin of this pair of reads.)
  //
  // `!is_w2` is load-bearing, not defensive. Rule 2b resolves a W2 contact
  // carrying a Thrive id to SKIP under the default decision WITHOUT clearing
  // the id — so `skipped` + an id is the state EVERY W2 row lands in after the
  // first run, and claiming it here would make "Update anyway" unreachable
  // from the second run onward. W2 must therefore be excluded before this rule,
  // the same ordering the deleted `skipReason` had.
  if (row.export_status === "skipped" && row.contact_id_thrive != null && !row.is_w2) {
    return false;
  }

  // A run is in flight for this row (the global Exporting state also guards).
  if (row.export_status === "pending") return false;

  // imported, failed (retry), W2 identity mismatch (selectable so the recruiter
  // can Update anyway; the server skips it by default), generic skipped.
  return true;
}

/**
 * Will this row actually be WRITTEN, given the decision in hand?
 *
 * The sibling of `isExportable`, and the distinction between them is the whole
 * point: one predicate was answering two different questions.
 *
 *   `isExportable`   — may the recruiter TICK this row?
 *   `willBeWritten`  — will ticking it produce a write?
 *
 * For a needs-review row those have different answers, and collapsing them is
 * what made the panel promise more than the worker delivered. The FE twin of
 * `resolve_export_plan` rules 2b (W2) and 2c (W3): both SKIP by default and
 * write only under an explicit per-contact `update_anyway` in `w2_decisions`.
 *
 * DECISION-AWARE, not a blanket subtraction. `carriedW2` threads the choices a
 * recruiter already made in Compare through to this panel, so a row they chose
 * to "Update anyway" must be counted — subtracting every conflict would
 * under-promise exactly the run they deliberately set up.
 *
 * `isExportable` is deliberately NOT changed to exclude W3. Its silence on
 * `is_w3` is load-bearing and test-pinned (`isExportable.test.ts`): excluding
 * W3 from SELECTION would make "Update anyway" unreachable for W3 rows from
 * this panel, which is the exact bug the W2 escape hatch exists to prevent.
 */
export function willBeWritten(row: ExportableRow, decision: W2Decision | undefined): boolean {
  if (!isExportable(row)) return false;
  // NEVER COMPARED (product decision 2026-09-17). Compare has not classified
  // this row, so nobody has checked whether the person is already in Thrive,
  // and `resolve_export_plan` SKIPs it with `not_compared_no_thrive_check`.
  //
  // HERE AND NOT IN `isExportable`, which is the same split this file is about.
  // The recruiter may still TICK such a row — they can go and compare it, and
  // the panel is expected to tell them how many of their selection will not be
  // added. A dead checkbox would leave nothing to tell them about.
  if (row.thrive_match_type == null) return false;
  const isConflict = Boolean(row.is_w2) || Boolean(row.is_w3);
  if (!isConflict) return true;
  // Written unless the decision is `skip`, rather than written only on
  // `update_anyway`. The wire union carries a THIRD arm (`add_as_new`, added
  // 2026-09-08) and `skip` is the only verb among them that does not write, so
  // testing for the non-writing case is what keeps this from silently
  // under-counting the next arm. `W2Decision` is imported from the generated
  // contract for the same reason `useExportToThrive` re-exports it instead of
  // restating the union: the hand-kept copy was the one place that did not go
  // red when the third arm landed.
  //
  // An ABSENT decision reads as `skip`, the same way the worker reads a
  // `w2_decisions` map that does not name the contact.
  return (decision ?? "skip") !== "skip";
}
